import { createFileRoute } from "@tanstack/react-router";
import { routeMeta, leafCanonicalLink } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { CollectionFlagship, type FlagshipContent } from "@/components/CollectionFlagship";
import { getCollection } from "@/data/collections";

const collection = getCollection("republic-series")!;

const content: FlagshipContent = {
  eyebrow: "Signature Collection · Government · Schools · Public Works",
  heroKicker:
    "Schools · Cities · Counties · Churches · San Antonio · Austin · Seguin · New Braunfels · Statewide Texas",
  badgeSubline:
    "School Districts • Municipalities • Counties • Parks & Rec • Churches • Public Agencies",
  lifestyleIntro:
    "Playgrounds kids can actually use in August, dugouts that keep players out of the sun, bus lanes and walkways that stay shaded from bell to bell — public shade built to spec, bid, and budget.",
  lifestyleBody:
    "The Republic Series is Texas Made Shade's government, school, and public-works division. We fabricate and install playground shade, cafeteria and walkway canopies, athletic dugout and bleacher shade, bus canopies, windscreens, and municipal awnings for school districts, cities, counties, parks departments, and churches across San Antonio, Austin, Houston, Corpus Christi, and the whole state. Welded aluminum frames, Sunbrella® and shade-mesh fabrics, permit-ready drawings, and quotes formatted for purchasing departments, co-op contracts, and formal bid packages.",
  solutionsHeading: "What's under the Republic Series™ badge",
  solutions: [
    { title: "Playground Shade Structures", desc: "Shade sails and canopies that cool playground equipment and surfacing.", to: "/playground-shade" },
    { title: "School Shade Structures", desc: "Cafeteria, courtyard, pickup-lane, and outdoor-classroom shade for ISD campuses.", to: "/school-shade-structures" },
    { title: "Athletics, Dugouts & Bleachers", desc: "Dugout tops, bleacher shade, and team-area canopies for baseball, softball, and track.", to: "/schools-athletics" },
    { title: "Church Shade Structures", desc: "Entry canopies, walkway covers, and courtyard shade for churches and ministries.", to: "/church-shade-structures" },
    { title: "Covered Walkways & Bus Canopies", desc: "Welded aluminum walkway and drop-off canopies that move students out of the sun and rain.", to: "/covered-parking" },
    { title: "Shade Sails & Tension Structures", desc: "Engineered sail shades sized for Texas wind loads on public sites.", to: "/sail-shades" },
    { title: "Municipal Awnings & Canopies", desc: "City hall, library, park pavilion, and public-facility awnings and signage canopies.", to: "/commercial-awnings" },
    { title: "Windscreens, Pit & Field Covers", desc: "Tennis and baseball windscreens, pit covers, and custom sewn athletic covers.", to: "/custom-fabrication" },
    { title: "Awning & Canopy Recover", desc: "Re-canvas aging campus and municipal canopies without replacing sound frames.", to: "/recover-awnings" },
    { title: "Custom Fabrication", desc: "One-off welded and sewn work for facilities, maintenance, and grounds departments.", to: "/custom-fabrication" },
  ],
  trustBadges: [
    "Permit-ready drawings on request",
    "Welded aluminum · public-spec frames",
    "Sunbrella® canvas & shade mesh",
    "Quotes formatted for bids & purchasing",
  ],
  faqs: [
    {
      q: "Do you provide quotes we can put into a formal bid or purchase order?",
      a: "Yes. Republic Series quotes are written for purchasing departments — itemized scope, fabric and frame specs, and drawings you can attach to a bid package or requisition.",
    },
    {
      q: "Can you supply engineered drawings for permitting?",
      a: "Yes — we provide drawings and load information suitable for permit submittal in most Texas jurisdictions, including school district facilities review.",
    },
    {
      q: "Which districts and cities do you serve?",
      a: "School districts, cities, and counties across San Antonio, Austin, Seguin, New Braunfels, San Marcos, Boerne, Kerrville, Houston, Corpus Christi, Victoria, Laredo, and statewide Texas.",
    },
    {
      q: "How long does playground or dugout shade last in Texas sun?",
      a: "Our welded aluminum frames are built for 20+ years, and we use UV-stabilized shade mesh or Sunbrella® canvas tops that can be recovered later without replacing the structure.",
    },
    {
      q: "Can you install over summer break or between seasons?",
      a: "That's how most campus work happens. Tell us your calendar and we'll schedule fabrication so install lands during break, off-season, or after hours.",
    },
    {
      q: "Do you repair or recover shade structures you didn't build?",
      a: "Yes. Torn sails, faded canopies, and damaged dugout tops get re-canvased or re-sewn — usually far cheaper than replacing the whole structure.",
    },
  ],
};

export const Route = createFileRoute("/republic-series")({
  head: (ctx) => ({
    meta: routeMeta(ctx, {
      title: "Republic Series™ — School, Playground & Municipal Shade | Texas Made Shade",
      description:
        "Republic Series™ — bid-ready school, playground, dugout, walkway and municipal shade structures for Texas ISDs, cities and counties. San Antonio, Austin, statewide.",
      keywords:
        "school shade structures texas, playground shade san antonio, playground shade austin, dugout shade covers, bleacher shade structures, covered walkway canopy school, bus canopy texas, municipal awnings texas, city park shade structures, church shade structures, isd shade structure bid, government shade structures texas, athletic windscreens, public works canopy",
    }),
    links: leafCanonicalLink(ctx, collection.path),
    scripts: serviceHeadScripts({
      path: collection.path,
      serviceName: "Republic Series Government, School & Public Works Shade",
      serviceType: "School, playground, athletic & municipal shade structures",
      description: collection.description,
      faqs: content.faqs,
      breadcrumbName: "Republic Series — Government, Schools & Public Works",
    }),
  }),
  component: () => <CollectionFlagship collection={collection} content={content} />,
});
